import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Quote,
  Grid3x3,
  Code,
  ChevronDown,
  Link as LinkIcon,
  X,
  Trash2,
  Plus as PlusIcon,
  Table as TableIcon,
} from 'lucide-react';

import { RiAttachment2 } from 'react-icons/ri';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TiptapImage from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import Typography from '@tiptap/extension-typography';
import { Menu, MenuButton, MenuItem, MenuItems, Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import getSuggestionConfig from './suggestion';
import { ProjectUser } from 'types/project';
import { toast } from 'react-hot-toast';
import { SmartLink } from './extensions/SmartLink';
import { VideoEmbed } from './extensions/VideoEmbed';
import { ContactOwner } from 'types/crm/contacts';
import { normalizeEmptyParagraphs } from 'utils/helpers';

interface TipTapEditorProps {
  initialContent?: string;
  onChange?: (content: string) => void;
  onBlur?: (content: string) => void;
  onFilesSelect?: (files: File[]) => void;
  onImageUpload?: (file: File) => Promise<string>;
  // Uploads a video and returns a reference to it — never a playable url
  // (see ResolvedVideo.tsx for why). When provided in comment mode, a
  // selected video is embedded inline (same as images); otherwise it falls
  // through to onFilesSelect as a generic attachment.
  onVideoUpload?: (file: File) => Promise<{ mediaUuid: string; projectUuid: string; fileName?: string }>;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  // mentionUsers?: ProjectUser[];
  mentionUsers?: (ProjectUser | ContactOwner)[];
  isComment?: boolean;
  onImagePreview?: (url: string) => void;
  autoFocus?: boolean;
}

const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
const ALLOWED_TYPES = [
  'image/png', 'image/jpeg', 'image/gif',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv', 'text/plain',
  ...VIDEO_TYPES,
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
// Screen recordings routinely exceed the 10MB cap other attachments use —
// a separate, larger limit for video specifically.
const MAX_VIDEO_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const CustomImage = TiptapImage.extend({
  draggable: true,
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: '100%',
        parseHTML: (element) => element.style.width || '100%',
        renderHTML: (attributes) => ({
          style: `width: ${attributes.width}; max-height: 350px; object-fit: contain; transition: all 0.2s ease-in-out;`,
        }),
      },
    };
  },
});

/**
 * Resizes and compresses an image file to a maximum dimension and quality.
 * Returns a Base64 string of the processed image.
 */
const resizeImage = (file: File, maxWidth = 1200, maxHeight = 1200): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => reject(new Error('Failed to load image for resizing'));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
  });
};

const ToolbarButton: React.FC<{
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title: string;
}> = ({ onClick, isActive = false, disabled = false, children, title }) => (
  <button
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    disabled={disabled}
    title={title}
    className={`
      p-2 rounded hover:bg-gray-600 transition-colors duration-200
      ${isActive ? 'bg-gray-600 text-white' : 'text-gray-300'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    `}
  >
    {children}
  </button>
);

const HeadingSelector = ({ editor }: { editor: any }) => {
  if (!editor) return null;

  const options = [
    { label: 'Normal text', value: 0, shortcut: '⌘⌥0', icon: 'Tt', textClass: 'text-sm font-normal' },
    { label: 'Heading 1',   value: 1, shortcut: '⌘⌥1', icon: 'H₁', textClass: 'text-base font-bold' },
    { label: 'Heading 2',   value: 2, shortcut: '⌘⌥2', icon: 'H₂', textClass: 'text-sm font-bold'   },
    { label: 'Heading 3',   value: 3, shortcut: '⌘⌥3', icon: 'H₃', textClass: 'text-sm font-semibold' },
    { label: 'Heading 4',   value: 4, shortcut: '⌘⌥4', icon: 'H₄', textClass: 'text-xs font-semibold' },
    { label: 'Heading 5',   value: 5, shortcut: '⌘⌥5', icon: 'H₅', textClass: 'text-xs font-medium'  },
    { label: 'Heading 6',   value: 6, shortcut: '⌘⌥6', icon: 'H₆', textClass: 'text-[11px] font-medium text-gray-400' },
  ];

  const handleApply = (value: number) => {
    if (value === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level: value as any }).run();
    }
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-blue-500/50 bg-blue-500/10 hover:bg-blue-500/20 transition-all duration-150 group"
      >
        <span className="text-base font-bold text-blue-400 group-hover:text-blue-300">Tt</span>
        <ChevronDown size={14} className="text-blue-500/70 group-hover:text-blue-400" />
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems 
          anchor="bottom start"
          className="z-[100] mt-2 rounded-xl bg-[#1e2230] border border-primary-subtle shadow-2xl py-2 w-64 focus:outline-none"
        >
          {options.map((option) => {
            const isActive = option.value === 0
              ? !editor.isActive('heading')
              : editor.isActive('heading', { level: option.value as any });

            return (
              <MenuItem key={option.label}>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => handleApply(option.value)}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 transition-colors duration-100 ${
                      isActive
                        ? 'bg-blue-600/20 text-blue-300'
                        : focus ? 'bg-white/5 text-gray-100' : 'text-gray-300'
                    }`}
                  >
                    <span className="w-6 text-center text-[11px] text-gray-500 font-semibold shrink-0 tabular-nums">
                      {option.icon}
                    </span>
                    <span className={`flex-1 text-left ${option.textClass}`}>
                      {option.label}
                    </span>
                    <span className="text-[10px] text-gray-600 font-mono shrink-0">
                      {option.shortcut}
                    </span>
                  </button>
                )}
              </MenuItem>
            );
          })}
        </MenuItems>
      </Transition>
    </Menu>
  );
};


// ─── Smart Link Popover ────────────────────────────────────────────────────────
const SmartLinkButton: React.FC<{ editor: any }> = ({ editor }) => {
  const [urlValue, setUrlValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const insertLink = (close: () => void) => {
    const raw = urlValue.trim();
    if (!raw) return;
    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    editor?.chain().focus().setSmartLink({ href }).run();
    setUrlValue('');
    close();
  };

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton
            className={`
              p-2 rounded hover:bg-gray-600 transition-colors duration-200
              ${open ? 'bg-gray-600 text-white' : 'text-gray-300'}
            `}
            title="Insert Smart Link"
          >
            <LinkIcon size={18} />
          </PopoverButton>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel
              anchor="bottom"
              className="z-[100] mt-2 w-[320px] rounded-xl border border-primary-subtle bg-[#1e2230] shadow-2xl p-4 focus:outline-none"
            >
              <div className="flex flex-col gap-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                  Insert link — internal or external
                </p>
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    autoFocus
                    type="url"
                    value={urlValue}
                    onChange={(e) => setUrlValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); insertLink(close); }
                      if (e.key === 'Escape') close();
                    }}
                    placeholder="https://..."
                    className="flex-1 px-3 py-2 rounded-lg bg-[#0f172a] border border-primary-subtle text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all"
                  />
                  <button
                    onClick={() => insertLink(close)}
                    disabled={!urlValue.trim()}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

const TableControls = ({ editor }: { editor: any }) => {
  if (!editor || !editor.isActive('table')) return null;

  return (
    <div className="flex items-center gap-1 bg-blue-600/10 rounded-md px-1">
      <ToolbarButton
        onClick={() => editor.chain().focus().addRowBefore().run()}
        title="Add Row Above"
      >
        <div className="relative">
          <PlusIcon size={12} className="absolute -top-1 -right-1 text-blue-400" />
          <TableIcon size={16} />
        </div>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().addRowAfter().run()}
        title="Add Row Below"
      >
        <div className="relative">
          <PlusIcon size={12} className="absolute -bottom-1 -right-1 text-blue-400" />
          <TableIcon size={16} />
        </div>
      </ToolbarButton>
      
      <div className="w-px h-4 bg-gray-600 mx-1"></div>

      <ToolbarButton
        onClick={() => editor.chain().focus().addColumnBefore().run()}
        title="Add Column Before"
      >
         <TableIcon size={16} className="rotate-90" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().addColumnAfter().run()}
        title="Add Column After"
      >
         <TableIcon size={16} className="-rotate-90" />
      </ToolbarButton>

      <div className="w-px h-4 bg-gray-600 mx-1"></div>

      <ToolbarButton
        onClick={() => {
           editor.chain().focus().deleteTable().run();
        }}
        title="Delete Entire Table"
      >
        <Trash2 size={16} className="text-red-400" />
      </ToolbarButton>
    </div>
  );
};



// ─── Main Editor Component ────────────────────────────────────────────────────
const TipTapEditor: React.FC<TipTapEditorProps> = ({
  initialContent = '',
  onChange,
  onBlur,
  onFilesSelect,
  onImageUpload,
  onVideoUpload,
  placeholder = 'Start typing...',
  className = '',
  mentionUsers = [],
  isComment = false,
  minHeight = '400px',
  onImagePreview,
  autoFocus = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [, setSelectionChangeCount] = useState(0);

  // Use a Ref to hold the latest mentionUsers so Tiptap's closure evaluates it dynamically whenever queried
  const mentionUsersRef = useRef(mentionUsers);
  useEffect(() => {
    mentionUsersRef.current = mentionUsers;
  }, [mentionUsers]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      // SmartLink replaces the standard Link extension with a rich preview NodeView
      SmartLink,
      Placeholder.configure({
        placeholder: placeholder,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'bg-blue-600/20 text-blue-500 rounded px-1 font-medium',
        },
        suggestion: getSuggestionConfig(() => mentionUsersRef.current),
      }),
      Typography,
      CustomImage.configure({
        allowBase64: true,
        HTMLAttributes: {
          class: 'h-auto rounded-lg shadow-sm my-4 cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all',
        },
      }),
      VideoEmbed,
    ],
    content: initialContent || '',
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        class:
          'prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
      handleDOMEvents: {
        click: (view, event) => {
          const target = event.target as HTMLElement;
          if (target.tagName === 'IMG' && onImagePreview) {
            const src = (target as HTMLImageElement).src;
            if (src) {
              onImagePreview(src);
              return true; // Prevent internal ProseMirror handling from blocking the signal
            }
          }
          return false;
        },
      },
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const files = Array.from(event.dataTransfer.files);
          const hasImages = files.some(file => file.type.startsWith('image/'));

          if (hasImages) {
            event.preventDefault();
            files.forEach(async (file) => {
              if (file.type.startsWith('image/')) {
                try {
                  const optimizedBase64 = await resizeImage(file);
                  editor.chain().focus().setImage({ src: optimizedBase64 }).run();
                } catch (error) {
                  console.error('Image optimization failed:', error);
                  const reader = new FileReader();
                  reader.onload = (readerEvent) => {
                    const base64 = readerEvent.target?.result as string;
                    editor.chain().focus().setImage({ src: base64 }).run();
                  };
                  reader.readAsDataURL(file);
                }
              }
            });
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const hasImages = items.some(item => item.type.startsWith('image/'));

        if (hasImages) {
          event.preventDefault();
          items.forEach(async (item) => {
            if (item.type.startsWith('image/')) {
              const file = item.getAsFile();
              if (file) {
                try {
                  const optimizedBase64 = await resizeImage(file);
                  editor.chain().focus().setImage({ src: optimizedBase64 }).run();
                } catch (error) {
                  console.error('Image optimization failed:', error);
                  const reader = new FileReader();
                  reader.onload = (readerEvent) => {
                    const base64 = readerEvent.target?.result as string;
                    editor.chain().focus().setImage({ src: base64 }).run();
                  };
                  reader.readAsDataURL(file);
                }
              }
            }
          });
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = normalizeEmptyParagraphs(editor.getHTML());
      lastEmittedHtml.current = html;
      onChange?.(html);
    },
    onBlur: ({ editor }) => {
      onBlur?.(normalizeEmptyParagraphs(editor.getHTML()));
    },
    onSelectionUpdate: () => {
      setSelectionChangeCount(prev => prev + 1);
    },
    onTransaction: () => {
      setSelectionChangeCount(prev => prev + 1);
    },
  });

  const lastEmittedHtml = useRef(initialContent);

  // Sync external changes (like clearing the input from the parent) back into TipTap
  useEffect(() => {
    if (!editor || initialContent === undefined) return;

    const normalizedInput = initialContent === '<p></p>' || initialContent === '<p></p>\n' ? '' : initialContent;
    const currentHtml = editor.getHTML();
    const normalizedCurrent = currentHtml === '<p></p>' || currentHtml === '<p></p>\n' ? '' : currentHtml;

    if (normalizedInput === lastEmittedHtml.current) return;

    if (editor.isFocused && normalizedInput !== '') return;

    if (normalizedInput !== normalizedCurrent) {
      editor.commands.setContent(initialContent);
      lastEmittedHtml.current = normalizedInput;
    }
  }, [initialContent, editor]);


  const handleUploadDoc = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !editor) return;

    const validFiles: File[] = [];

    for (const file of files) {
      const isVideo = VIDEO_TYPES.includes(file.type);
      const sizeLimit = isVideo ? MAX_VIDEO_FILE_SIZE : MAX_FILE_SIZE;
      if (file.size > sizeLimit) {
        toast.error(`${file.name} exceeds the ${isVideo ? '100MB' : '10MB'} limit.`);
        continue;
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name} type is not supported.`);
        continue;
      }

      if (isComment && file.type.startsWith('image/')) {
        try {
          if (onImageUpload) {
            const url = await onImageUpload(file);
            editor.chain().focus().setImage({ src: url }).run();
          } else {
            const optimizedBase64 = await resizeImage(file);
            editor.chain().focus().setImage({ src: optimizedBase64 }).run();
          }
        } catch (error) {
          console.error('Image upload/optimization failed:', error);
          const reader = new FileReader();
          reader.onload = (readerEvent) => {
            const base64 = readerEvent.target?.result as string;
            editor.chain().focus().setImage({ src: base64 }).run();
          };
          reader.readAsDataURL(file);
        }
      } else if (isComment && isVideo && onVideoUpload) {
        try {
          const { mediaUuid, projectUuid, fileName } = await onVideoUpload(file);
          editor.chain().focus().insertContent({
            type: 'videoEmbed',
            attrs: { mediaUuid, projectUuid, fileName: fileName || file.name },
          }).run();
        } catch (error) {
          console.error('Video upload failed:', error);
          toast.error(`Failed to upload ${file.name}.`);
        }
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0 && onFilesSelect) {
      onFilesSelect(validFiles);
    }

    e.target.value = '';
  };


  return (
    <div
      className={`w-full mx-auto bg-gray-800 rounded-lg shadow-lg ${className}`}
    >
      {editor && (
        <>
          {/* Toolbar */}
      <div className={`relative bg-primary-variant border-b border-primary-subtle p-3 overflow-visible ${!isComment ? 'sticky top-0 z-30' : 'z-30'}`}>
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar" style={{ flexWrap: 'nowrap' }}>
          {/* Text Format Dropdown */}
          <HeadingSelector editor={editor} />

          <div className="w-px h-6 bg-gray-600/50 mx-2"></div>

          {/* Basic Formatting */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title="Bold (Ctrl+B)"
          >
            <Bold size={18} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title="Italic (Ctrl+I)"
          >
            <Italic size={18} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            title="Underline (Ctrl+U)"
          >
            <UnderlineIcon size={18} />
          </ToolbarButton>

          <div className="w-px h-6 bg-gray-600 mx-2"></div>

          {/* Lists */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <List size={18} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <ListOrdered size={18} />
          </ToolbarButton>

          <div className="w-px h-6 bg-gray-600 mx-2"></div>

          {/* Quote */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title="Quote"
          >
            <Quote size={18} />
          </ToolbarButton>

          {/* Smart Link */}
          <SmartLinkButton editor={editor} />

          {/* Table */}
          <ToolbarButton
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
            isActive={editor.isActive('table')}
            title="Insert Table"
          >
            <Grid3x3 size={18} />
          </ToolbarButton>

           <ToolbarButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
            title="Code Block"
          >
            <Code size={18} />
          </ToolbarButton>

          <ToolbarButton
            title={isComment ? "Attach Image or Video" : "Upload Document/Image/Video"}
            onClick={handleUploadDoc}
          >
            <RiAttachment2 size={18} />
          </ToolbarButton>

          {editor && <TableControls editor={editor} />}


          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept={isComment ? "image/*,video/*" : ".pdf,.doc,.docx,.png,.jpg,.jpeg,.mp4,.mov,.avi,.webm"}
            onChange={handleFileChange}
          />
        </div>
      </div>

      {/* Editor Content */}
      <div
        className={`bg-primary-variant tableWrapper text-gray-100 cursor-text rounded-b-lg ${className}`}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent
          editor={editor}
          className="tiptap prose prose-invert max-w-none p-4 focus:outline-none"
          style={{
            minHeight: className ? undefined : minHeight,
          }}
        />
      </div>
        </>
      )}
    </div>
  );
};

export default TipTapEditor;
